import i18n from "@/lib/i18n";
import { useState } from "react";
import { Building, ShieldCheck, Loader2, AlertTriangle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

import { tAuto } from "@/lib/tAuto";
interface IbanFormProps {
  userId: string;
  currentIban?: string | null;
  ibanConfirmedAt?: string | null;
  onSaved?: () => void;
}

const ANGOLAN_BANKS = [
  "BAI - Banco Angolano de Investimentos",
  "BFA - Banco de Fomento Angola",
  "BIC - Banco BIC",
  "BMA - Banco Millennium Atlântico",
  "BPC - Banco de Poupança e Crédito",
  "BCA - Banco Comercial Angolano",
  "Banco SOL",
  "Banco Keve",
  "Banco Yetu",
  "Standard Bank Angola",
  "Banco Caixa Geral Angola",
  "Banco VTB África",
  "Banco Prestígio",
  "Banco de Negócios Internacional (BNI)",
  "Finibanco Angola",
  "Banco Valor",
  "Outro",
];

const IbanForm = ({ userId, currentIban, ibanConfirmedAt, onSaved }: IbanFormProps) => {
  const [bankName, setBankName] = useState("");
  const [iban, setIban] = useState("");
  const [confirmIban, setConfirmIban] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { toast } = useToast();

  const formatIban = (value: string) => {
    return value.replace(/\s/g, "").toUpperCase();
  };

  const handleSubmit = () => {
    const formattedIban = formatIban(iban);
    const formattedConfirm = formatIban(confirmIban);

    if (!bankName) {
      toast({ title: tAuto("auto.components_iban_ibanform.1", "Banco obrigatório"), description: "Por favor, selecione o nome do banco.", variant: "destructive" });
      return;
    }

    if (!formattedIban) {
      toast({ title: tAuto("auto.components_iban_ibanform.2", "IBAN obrigatório"), description: "Por favor, insira o seu IBAN.", variant: "destructive" });
      return;
    }

    if (formattedIban !== formattedConfirm) {
      toast({ title: tAuto("auto.components_iban_ibanform.3", "IBAN não corresponde"), description: tAuto("auto.components_iban_ibanform.4", "Os dois campos de IBAN devem ser iguais."), variant: "destructive" });
      return;
    }

    if (formattedIban.length < 15) {
      toast({ title: tAuto("auto.components_iban_ibanform.5", "IBAN inválido"), description: "O IBAN inserido parece ser muito curto.", variant: "destructive" });
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          iban: formatIban(iban),
          iban_confirmed_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) throw error;

      toast({
        title: tAuto("auto.components_iban_ibanform.6", "IBAN guardado com sucesso"),
        description: i18n.t("components.ibanRegistered") || "O seu IBAN foi registado. Os saques serão processados em 1 a 3 dias úteis.",
      });

      setIban("");
      setConfirmIban("");
      setBankName("");
      onSaved?.();
    } catch (error) {
      console.error("Error saving IBAN:", error);
      toast({
        title: i18n.t("components.saveError") || "Erro ao guardar",
        description: i18n.t("components.saveErrorDesc") || "Não foi possível guardar o IBAN. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (currentIban && ibanConfirmedAt) {
    const maskedIban = currentIban.slice(0, 4) + " •••• •••• " + currentIban.slice(-4);
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building className="w-5 h-5 text-primary" />
            IBAN para Saques
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
            <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-mono font-medium text-sm">{maskedIban}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Confirmado em {new Date(ibanConfirmedAt).toLocaleDateString("pt-AO")}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Os saques via transferência bancária são processados em 1 a 3 dias úteis.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building className="w-5 h-5 text-primary" />
            Adicionar IBAN para Saques
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/20">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Adicione uma conta bancária para receber saques via transferência bancária. O prazo de processamento e transferência é de 1 a 3 dias úteis.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bank-name" className="text-sm">Nome do Banco</Label>
            <Select value={bankName} onValueChange={setBankName}>
              <SelectTrigger id="bank-name">
                <SelectValue placeholder="Selecione o banco" />
              </SelectTrigger>
              <SelectContent>
                {ANGOLAN_BANKS.map(bank => (
                  <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="iban" className="text-sm">IBAN</Label>
            <Input
              id="iban"
              placeholder="AO06 0000 0000 0000 0000 0000 0"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              className="font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-iban" className="text-sm">Confirmar IBAN</Label>
            <Input
              id="confirm-iban"
              placeholder="Repita o IBAN"
              value={confirmIban}
              onChange={(e) => setConfirmIban(e.target.value)}
              className="font-mono"
            />
          </div>

          <p className="text-[11px] text-muted-foreground">
            O nome da conta bancária deve corresponder ao nome registado na BeraStays.
          </p>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !iban || !confirmIban || !bankName}
            className="w-full gradient-primary text-white"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : null}
            Guardar IBAN
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Ao confirmar, o IBAN <strong className="font-mono">{formatIban(iban)}</strong> ({bankName}) será associado à sua conta para receber saques.
              </p>
              <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                <p className="text-sm font-medium text-destructive">
                  Atenção: Só pode utilizar um IBAN em seu nome, registado na plataforma. O uso de IBAN de terceiros não é permitido e pode resultar no bloqueio da sua conta.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tAuto("auto.components_iban_ibanform.7", "Cancelar")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className="gradient-primary text-white">
              Confirmar IBAN
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default IbanForm;
